import { apiClient } from '@/api/client';
import type { Invitation, CreateInvitationData } from '@/types/staff.types';

export const invitationsApi = {
  // Create a new invitation
  create: async (data: CreateInvitationData) => {
    // `emailSent: false` means the invitation was saved but the mail did not
    // go out — the caller must say so rather than report success.
    const response = await apiClient.post<Invitation & { emailSent: boolean }>(
      `/invitations`,
      data,
    );
    return response.data;
  },

  // Get all invitations for an clinic
  getByClinic: async () => {
    const response = await apiClient.get<Invitation[]>(`/invitations/clinic`);
    return response.data;
  },

  // Get invitations for the currently logged-in user (by JWT identity)
  getMyInvitations: async () => {
    const response = await apiClient.get<Invitation[]>(`/invitations/my`);
    return response.data;
  },

  /**
   * Accept an invitation. The acceptor is the signed-in user — the server
   * reads it from the session cookie. It used to be a path segment, which
   * meant this call could not be made until `AuthContext` had loaded a user
   * object, and on the one screen that matters (an account with no clinic
   * yet, so no `/auth/me`) it never had one.
   */
  accept: async (tokenOrId: string) => {
    // Either the token from the emailed link or the invitation's id from
    // /invitations/my; the server only acts for the invited address.
    const response = await apiClient.post(`/invitations/accept/${tokenOrId}`);
    return response.data;
  },

  /** Decline an invitation. Identity comes from the session, as with accept. */
  decline: async (tokenOrId: string) => {
    const response = await apiClient.post(`/invitations/decline/${tokenOrId}`);
    return response.data;
  },

  /**
   * Re-send a pending invitation's email and extend its expiry.
   * The wait before each resend grows with every successful send; inside it
   * the server answers 429 with `nextResendAt`. A 503 means the mail failed
   * and the attempt was not counted.
   */
  resend: async (id: string) => {
    const response = await apiClient.post<{
      id: string;
      lastSentAt: string;
      expiresAt: string;
      sendCount: number;
      nextResendAt: string | null;
    }>(`/invitations/${id}/resend`);
    return response.data;
  },

  /** Withdraw an invitation that has not been accepted. */
  revoke: async (id: string) => {
    const response = await apiClient.delete<{ id: string; revoked: boolean }>(
      `/invitations/${id}`,
    );
    return response.data;
  },

  // Check invitation
  check: async (token: string) => {
    const response = await apiClient.post('/invitations/check', { token });
    return response.data;
  },
};
